import React, { useEffect, useMemo, useState } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded'
import { DateTime } from 'luxon'
import { Box, Chip, IconButton, Tooltip, Typography, alpha } from '@mui/material'
import { AssignableQuestionnaire } from 'src/graphql.generated'
import { formatDays, relativeFrom, toIso } from '../format'
import { useAdvancedPatientHistoryState } from '../api/useAdvancedPatientHistory'
import {
  GetAdvancedSymptomSurveyAssignmentsDocument,
  useAssignAdvancedSymptomSurveyMutation,
  useGetAdvancedSymptomSurveyAssignmentsQuery,
  useRemoveAdvancedSymptomSurveyAssignmentMutation,
} from '../api/symptomSurveys.generated'
import { onDark } from '../theme'
import AddSymptomSurveyDialog from './AddSymptomSurveyDialog'
import SurveyDetailDialog from './SurveyDetailDialog'
import { QUESTIONNAIRE_LABELS } from './questionnaireLabels'
import { scoredInstancesFor } from './questionnaireHistory'
import { ExampleTag } from '../tour/examples'
import { useTourExample } from '../tour/tourExample'
import EmptyNote from './EmptyNote'
import Section from './Section'

type SurveyChipProps = {
  label: string
  completed: boolean
  completionCount: number
  hasHistory: boolean
  sentLabel: string | null
  scheduledFor?: string | null
  repeatCadenceInDays?: number | null
  onClick?: () => void
}

const SurveyChip = ({
  label,
  completed,
  completionCount,
  hasHistory,
  sentLabel,
  scheduledFor,
  repeatCadenceInDays,
  onClick,
}: SurveyChipProps) => {
  let tooltip = sentLabel ? `Assigned ${sentLabel}.` : 'Assigned.'
  if (completed) {
    tooltip = `${completionCount} past ${
      completionCount === 1 ? 'completion' : 'completions'
    }. No future completion scheduled.`
  } else if (repeatCadenceInDays) {
    const daysUntilDue = scheduledFor ? Math.max(1, Math.ceil(DateTime.fromISO(scheduledFor).diffNow('days').days)) : 0
    const nextCompletion = daysUntilDue
      ? `Next completion scheduled in ${formatDays(daysUntilDue)}.`
      : 'Next completion available now.'
    tooltip = `Recurs every ${formatDays(repeatCadenceInDays)}. ${nextCompletion}`
  }
  const clickHint = `Click to learn more${hasHistory ? ' and see history' : ''}.`
  return (
    <Tooltip describeChild title={`${tooltip} ${clickHint}`}>
      <Chip
        size="small"
        variant={completed ? 'outlined' : 'filled'}
        clickable={!!onClick}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{label}</span>
            {!completed && !!repeatCadenceInDays && (
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  display: 'inline-grid',
                  placeItems: 'center',
                  position: 'relative',
                  width: 24,
                  height: 24,
                  fontSize: 8.5,
                  fontWeight: 700,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <RepeatRoundedIcon sx={{ position: 'absolute', fontSize: 24 }} />
                {repeatCadenceInDays}
              </Box>
            )}
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                minWidth: 18,
                px: 0.5,
                borderRadius: 9,
                backgroundColor: completed ? onDark.surface : onDark.stroke,
                color: completed ? onDark.secondary : onDark.primary,
                fontSize: 11,
                fontWeight: 600,
                lineHeight: '18px',
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {completionCount}
            </Box>
          </Box>
        }
        onClick={onClick}
        sx={{
          backgroundColor: completed ? onDark.stroke : onDark.accentGreen,
          color: completed ? onDark.secondary : onDark.stroke,
          borderColor: completed ? onDark.line : onDark.accentGreen,
          fontWeight: completed ? 400 : 600,
          '&.MuiChip-clickable:hover': {
            backgroundColor: completed ? onDark.surface : alpha(onDark.accentGreen, 0.9),
          },
          '&.Mui-focusVisible': { outline: `2px solid ${onDark.accentBlue}`, outlineOffset: 2 },
        }}
      />
    </Tooltip>
  )
}

type Props = { uid: string; timezone: string | null | undefined; grouped?: boolean; canManageSurveys: boolean }

const SURVEY_REFRESH_INTERVAL_MS = 15_000

const SymptomSurveysSection = ({ uid, timezone, grouped = false, canManageSurveys }: Props) => {
  const { data, loading, error } = useGetAdvancedSymptomSurveyAssignmentsQuery({
    pollInterval: SURVEY_REFRESH_INTERVAL_MS,
    fetchPolicy: 'cache-and-network',
    variables: { uid },
  })
  const assignments = data?.patientQuestionnaireAssignments || []
  const isActive = (assignment: (typeof assignments)[number]) =>
    assignment.repeatCadenceInDays != null || !assignment.completedAt
  // Only an active assignment can produce a new result, so the full history is re-read only while one exists.
  const { entries } = useAdvancedPatientHistoryState(uid, {
    pollInterval: assignments.some(isActive) ? SURVEY_REFRESH_INTERVAL_MS : 0,
    fetchPolicy: 'cache-and-network',
  })
  const instancesByQuestionnaire = useMemo(
    () =>
      new Map(
        Object.values(AssignableQuestionnaire).map((questionnaire) => [
          questionnaire,
          scoredInstancesFor(questionnaire, entries),
        ])
      ),
    [entries]
  )
  const currentByQuestionnaire = new Map<AssignableQuestionnaire, (typeof assignments)[number]>()
  assignments.forEach((assignment) => {
    const current = currentByQuestionnaire.get(assignment.questionnaire)
    const isNewer = (toIso(assignment.assignedAt) ?? '') > (toIso(current?.assignedAt) ?? '')
    if (
      !current ||
      (isActive(assignment) && !isActive(current)) ||
      (isActive(assignment) === isActive(current) && isNewer)
    ) {
      currentByQuestionnaire.set(assignment.questionnaire, assignment)
    }
  })
  const surveys = Object.values(AssignableQuestionnaire).flatMap((questionnaire) => {
    const assignment = currentByQuestionnaire.get(questionnaire)
    const instances = instancesByQuestionnaire.get(questionnaire) ?? []
    return assignment || instances.length
      ? [
          {
            questionnaire,
            assignment,
            instances,
            completed: !assignment || !isActive(assignment),
          },
        ]
      : []
  })
  const refetchAssignments = [{ query: GetAdvancedSymptomSurveyAssignmentsDocument, variables: { uid } }]
  const [removeAssignment] = useRemoveAdvancedSymptomSurveyAssignmentMutation({ refetchQueries: refetchAssignments })
  const [assignSurvey] = useAssignAdvancedSymptomSurveyMutation({ refetchQueries: refetchAssignments })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<AssignableQuestionnaire | null>(null)
  const showsExample = useTourExample() === 'symptom-survey' && surveys.length === 0 && !loading && !error

  useEffect(() => {
    setDialogOpen(false)
    setSelectedQuestionnaire(null)
  }, [uid, canManageSurveys])

  const openQuestionnaires = assignments.filter(isActive).map((assignment) => assignment.questionnaire)

  const selected = surveys.find((survey) => survey.questionnaire === selectedQuestionnaire)
  const scheduledFor = (assignment: (typeof assignments)[number] | undefined) => {
    const dueAt = toIso(assignment?.dueAt)
    return assignment && isActive(assignment) && dueAt && DateTime.fromISO(dueAt) > DateTime.now() ? dueAt : null
  }
  let emptyCopy = 'None prescribed'
  if (error) {
    emptyCopy = 'Failed to load surveys'
  } else if (loading) {
    emptyCopy = '…'
  }

  return (
    <Section
      grouped={grouped}
      divided={grouped}
      title="Symptom surveys"
      action={
        canManageSurveys && (
          <Tooltip title="Add symptom survey">
            <IconButton
              onClick={() => setDialogOpen(true)}
              size="small"
              aria-label="Add symptom survey"
              sx={{ color: onDark.secondary }}
            >
              <AddRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    >
      {canManageSurveys && (
        <AddSymptomSurveyDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          openQuestionnaires={openQuestionnaires}
          onAssign={(questionnaire, repeatCadenceInDays) =>
            assignSurvey({ variables: { uid, questionnaire, repeatCadenceInDays } }).then(() => undefined)
          }
        />
      )}
      {selected && (
        <SurveyDetailDialog
          open
          onClose={() => setSelectedQuestionnaire(null)}
          label={QUESTIONNAIRE_LABELS[selected.questionnaire] || selected.questionnaire}
          completed={selected.completed}
          sentAt={toIso(selected.assignment?.assignedAt)}
          completedAt={toIso(selected.assignment?.completedAt) ?? selected.instances[0]?.createdAt ?? null}
          timezone={timezone}
          instances={selected.instances}
          scheduledFor={scheduledFor(selected.assignment)}
          repeatCadenceInDays={selected.assignment?.repeatCadenceInDays}
          onUnassign={
            canManageSurveys && selected.assignment && !selected.completed
              ? () =>
                  removeAssignment({ variables: { uid, assignmentID: selected.assignment!.assignmentID } }).then(
                    () => undefined
                  )
              : undefined
          }
          onAssignAgain={
            canManageSurveys && selected.completed && !openQuestionnaires.includes(selected.questionnaire)
              ? () => assignSurvey({ variables: { uid, questionnaire: selected.questionnaire } }).then(() => undefined)
              : undefined
          }
        />
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {surveys.length === 0 && !showsExample && <EmptyNote>{emptyCopy}</EmptyNote>}
        {[
          { title: 'Active', completed: false },
          { title: 'Completed', completed: true },
        ].map(({ title, completed }) => {
          const groupSurveys = surveys.filter((survey) => survey.completed === completed)
          if (groupSurveys.length === 0 && !showsExample) return null

          return (
            <Box key={title} component="section" aria-label={`${title} symptom surveys`}>
              <Typography component="h3" variant="caption" sx={{ color: onDark.secondary, fontWeight: 600, mb: 0.5 }}>
                {title}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                {showsExample && (
                  <SurveyChip
                    label={completed ? 'ASRM' : 'ISI'}
                    completed={completed}
                    completionCount={completed ? 2 : 0}
                    hasHistory={completed}
                    sentLabel={completed ? null : '2 days ago'}
                  />
                )}
                {groupSurveys.map(({ questionnaire, assignment, instances }) => (
                  <SurveyChip
                    key={questionnaire}
                    label={QUESTIONNAIRE_LABELS[questionnaire] || questionnaire}
                    completed={completed}
                    completionCount={Math.max(instances.length, assignment?.completedAt ? 1 : 0)}
                    hasHistory={instances.length > 0}
                    sentLabel={relativeFrom(toIso(assignment?.assignedAt))}
                    scheduledFor={scheduledFor(assignment)}
                    repeatCadenceInDays={assignment?.repeatCadenceInDays}
                    onClick={() => setSelectedQuestionnaire(questionnaire)}
                  />
                ))}
              </Box>
            </Box>
          )
        })}
        {showsExample && <ExampleTag />}
      </Box>
    </Section>
  )
}

export default SymptomSurveysSection
